import { Trans, useTranslation } from "react-i18next";
import { Button } from "~/components/ui/button";
import { Link } from "~/components/ui/link";
import { LINE_BREAKS } from "~/components/ui/max-width-line-break";
import { Picture } from "~/components/ui/picture";
import type { ProductPromoContent } from "~/data/products/types";

interface ProductPromoSectionProps {
  content: ProductPromoContent;
}

// One product, no price: headline, body, image and a "Learn More" link (Figma
// 10480:408062 mobile, 10480:408905 desktop). Stacked copy → image → link on mobile; from
// lg the image spans the left column and runs 28px under the right-aligned copy. Not CrossSellSection, which is a priced card grid. Colours come from
// the tokens, so it reads correctly inside the .fleet scope.
export function ProductPromoSection({ content }: ProductPromoSectionProps) {
  const { t } = useTranslation("products");

  return (
    <section aria-labelledby="pdp-promo-title" className="bg-background overflow-x-clip py-10">
      {/* Figma's 1022px row sits 15px left of centre at 1440. */}
      <div className="wrap flex flex-col items-center gap-3 text-center lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,558px)] lg:grid-rows-[auto_1fr] lg:items-start lg:gap-x-6 lg:gap-y-6 lg:text-right xl:w-[1022px] xl:max-w-none xl:translate-x-[-15px] xl:grid-cols-[464px_558px] xl:gap-x-0 xl:px-0">
        <div className="relative flex flex-col gap-2 lg:col-start-2 lg:row-start-1 lg:items-end lg:gap-4">
          <h2
            id="pdp-promo-title"
            className="font-display text-text-heading text-xl leading-[1.15] font-extrabold lg:text-4xl lg:leading-[1.1]"
          >
            <Trans t={t} i18nKey={content.headline} components={LINE_BREAKS} />
          </h2>
          <p className="text-text-body-secondary text-md leading-[1.1] lg:w-[470px]">{t(content.body)}</p>
        </div>
        <Picture
          src={content.image}
          alt={t(content.imageAlt)}
          width={492}
          className="h-[160px] w-[293px] object-cover lg:col-start-1 lg:row-span-2 lg:row-start-1 lg:aspect-[492/268] lg:h-auto lg:w-full lg:max-w-[492px] xl:h-[268px] xl:w-[492px] xl:max-w-none"
        />
        <Button
          asChild
          variant="secondary-alt"
          size="sm"
          className="h-11 w-full max-w-[311px] text-base leading-5 tracking-[0.16px] lg:col-start-2 lg:row-start-2 lg:h-[52px] lg:w-[187px] lg:justify-self-end"
        >
          <Link data-sid="pdp-promo-cta" to={content.ctaHref} aria-label={t(content.ariaLabel)}>
            {t(content.ctaLabel)}
          </Link>
        </Button>
      </div>
    </section>
  );
}
